import { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSearchParams } from 'react-router-dom';
import { BarChart3, Download } from 'lucide-react';
import { ErrorBlock, LoadingBlock } from '@/components/activities/ActivityUi';
import { EntriesChart, EquipmentHoursChart } from '@/components/reports/ReportCharts';
import ReportFilters from '@/components/reports/ReportFilters';
import {
  ActivitiesByType,
  ActivitiesByUniverso,
  ActivitiesTable,
  BadgesTable,
  CentresTable,
  Demographics,
  EquipmentTable,
} from '@/components/reports/ReportSections';
import StatTile from '@/components/reports/StatTile';
import { useDigitalCenters } from '@/hooks/useDigitalCenters';
import { useReportsOverview, type ReportFilter } from '@/hooks/useReports';
import { ApiValidationError } from '@/lib/api';
import { buildReportCsv, downloadCsv, reportFileName } from '@/lib/reportCsv';
import { formatCount, formatHours } from '@/lib/reportFormat';
import {
  DEFAULT_PRESET,
  formatRange,
  isPeriodPreset,
  parseDay,
  presetRange,
  toDay,
  type DayRange,
  type PeriodPreset,
} from '@/lib/reportPeriod';

const ALL_CENTRES = 'Todos os centros';

interface Filters {
  preset: PeriodPreset;
  range: DayRange;
  centerId: number;
}

/**
 * The filters as the address carries them, so that a report can be reloaded,
 * bookmarked or sent to someone and still be the same report. Anything the
 * address gets wrong falls back to the last thirty days of every centre.
 */
function readFilters(params: URLSearchParams, today: Date): Filters {
  const asked = params.get('periodo');
  const centre = Number(params.get('centro'));
  const centerId = Number.isInteger(centre) && centre > 0 ? centre : 0;

  const from = params.get('de') ?? '';
  const to = params.get('ate') ?? '';
  if (asked === 'custom' && parseDay(from) && parseDay(to)) {
    // Dates the wrong way round are put right, as the API does.
    const range = from <= to ? { from, to } : { from: to, to: from };
    return { preset: 'custom', range, centerId };
  }

  const preset = isPeriodPreset(asked) && asked !== 'custom' ? asked : DEFAULT_PRESET;
  return { preset, range: presetRange(preset, today)!, centerId };
}

function writeFilters(filters: Filters): URLSearchParams {
  const params = new URLSearchParams();
  if (filters.preset !== DEFAULT_PRESET) params.set('periodo', filters.preset);
  if (filters.preset === 'custom') {
    params.set('de', filters.range.from);
    params.set('ate', filters.range.to);
  }
  if (filters.centerId > 0) params.set('centro', String(filters.centerId));
  return params;
}

/**
 * What happened in the centres over a period: who came, to what, and what
 * they used. One row of filters scopes every figure on the page.
 */
export default function ReportsPage() {
  const [params, setParams] = useSearchParams();
  const today = useMemo(() => new Date(), []);
  const filters = readFilters(params, today);

  const centersQuery = useDigitalCenters({ limit: 100 });
  const centers = centersQuery.data?.digitalCenters ?? [];

  const filter: ReportFilter = { from: filters.range.from, to: filters.range.to, centerId: filters.centerId };
  const { data: report, isLoading, isError, error, isPlaceholderData, isFetching } = useReportsOverview(filter);

  const update = (next: Partial<Filters>) => setParams(writeFilters({ ...filters, ...next }), { replace: true });

  const centreName = centers.find((c) => c.id === filters.centerId)?.centerName ?? ALL_CENTRES;
  // A refusal of the period itself, not a failure to reach the API.
  const badPeriod = isError && error instanceof ApiValidationError;
  // What is on screen belongs to the filters before, while these load.
  const stale = isPlaceholderData && isFetching;

  return (
    <>
      <Helmet>
        <title>Relatórios | DIGGER</title>
      </Helmet>

      <div className="space-y-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
              <BarChart3 className="w-6 h-6 text-primary" />
              Relatórios / KPIs
            </h1>
            <p className="text-muted-foreground mt-1">
              {centreName}, {formatRange(filters.range)}.
            </p>
          </div>
          <button
            type="button"
            className="btn-outline gap-2 shrink-0"
            disabled={!report || stale}
            onClick={() => report && downloadCsv(reportFileName(report), buildReportCsv(report, centreName))}
          >
            <Download className="w-4 h-4" aria-hidden="true" />
            Exportar CSV
          </button>
        </div>

        <ReportFilters
          preset={filters.preset}
          range={filters.range}
          centerId={filters.centerId}
          centers={centers}
          today={toDay(today)}
          onPreset={(preset) => update({ preset, range: presetRange(preset, today) ?? filters.range })}
          onRange={(range) => update({ preset: 'custom', range })}
          onCenter={(centerId) => update({ centerId })}
        />

        {isLoading && <LoadingBlock />}

        {isError && !stale && (
          <ErrorBlock
            message={
              badPeriod
                ? 'O período escolhido não é válido. Um relatório cobre, no máximo, três anos.'
                : 'Não foi possível carregar o relatório. Tente novamente.'
            }
          />
        )}

        {report && !isError && (
          <div
            className={`space-y-6 transition-opacity ${stale ? 'opacity-50' : ''}`}
            aria-busy={stale}
            data-testid="report"
          >
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatTile
                label="Entradas"
                value={formatCount(report.summary.visits)}
                current={report.summary.visits}
                previous={report.previous.visits}
                detail={
                  report.summary.visitsByStaff > 0
                    ? `${formatCount(report.summary.visitsByStaff)} registadas pela equipa`
                    : undefined
                }
              />
              <StatTile
                label="Participantes diferentes"
                value={formatCount(report.summary.uniqueParticipants)}
                current={report.summary.uniqueParticipants}
                previous={report.previous.uniqueParticipants}
              />
              <StatTile
                label="Visitantes em grupo"
                value={formatCount(report.summary.visitors)}
                current={report.summary.visitors}
                previous={report.previous.visitors}
              />
              <StatTile
                label="Novos registos"
                value={formatCount(report.summary.newParticipants)}
                current={report.summary.newParticipants}
                previous={report.previous.newParticipants}
              />
              <StatTile
                label="Sessões de atividades"
                value={formatCount(report.summary.sessions)}
                current={report.summary.sessions}
                previous={report.previous.sessions}
              />
              <StatTile
                label="Presenças em atividades"
                value={formatCount(report.summary.attendance)}
                current={report.summary.attendance}
                previous={report.previous.attendance}
              />
              <StatTile
                label="Horas de equipamento"
                value={formatHours(report.summary.equipmentHours)}
                current={report.summary.equipmentHours}
                previous={report.previous.equipmentHours}
                detail={`${formatCount(report.summary.equipmentSessions)} utilizações`}
              />
              <StatTile
                label="Credenciais atribuídas"
                value={formatCount(report.summary.badgesAwarded)}
                current={report.summary.badgesAwarded}
                previous={report.previous.badgesAwarded}
              />
            </div>

            <EntriesChart report={report} />

            {filters.centerId === 0 && report.byCenter.length > 1 && <CentresTable report={report} />}

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              <ActivitiesByType report={report} />
              <ActivitiesByUniverso report={report} />
            </div>

            <ActivitiesTable report={report} />

            <EquipmentHoursChart report={report} />
            <EquipmentTable report={report} />

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              <Demographics report={report} />
              <BadgesTable report={report} />
            </div>
          </div>
        )}
      </div>
    </>
  );
}
